let overlay;

function getZoomStrings() {
  return window.accessibilityStrings;
}

function announceToScreenReader(message) {
  const region = document.getElementById('zoom-live-region');

  region.textContent = '';

  requestAnimationFrame(() => {
    region.textContent = message;
  });

  if (overlay) {
    showVisualAnnouncement(message);
  }
}

function showVisualAnnouncement(message) {
  if (!overlay) return;

  let zoomInstruction = null;

  const originalImage = overlay.nextElementSibling;
  if (
    originalImage &&
    originalImage.nextElementSibling &&
    originalImage.nextElementSibling.classList.contains('zoom-instruction')
  ) {
    zoomInstruction = originalImage.nextElementSibling;
  }

  if (!zoomInstruction) return;

  showInstructionMessage(zoomInstruction, message);
}

function showInstructionMessage(zoomInstruction, message) {
  zoomInstruction.classList.add('zoom-announcement');
  zoomInstruction.textContent = message;

  if (zoomInstruction.hideTimeout) {
    clearTimeout(zoomInstruction.hideTimeout);
  }

  zoomInstruction.hideTimeout = setTimeout(() => {
    if (zoomInstruction) {
      zoomInstruction.classList.remove('zoom-announcement');
    }
  }, 8000);
}

function createZoomInstruction(image, index) {
  if (image.dataset.zoomInstructionAttached) {
    return;
  }

  const instruction = document.createElement('div');
  const instructionId = `zoom-instruction-${index}`;
  instruction.id = instructionId;
  instruction.className = 'zoom-instruction';
  instruction.textContent = getZoomStrings().activate;

  const parent = image.parentElement;
  parent.insertBefore(instruction, image.nextSibling);

  const existingDescribedBy = image.getAttribute('aria-describedby');
  const newDescribedBy = existingDescribedBy ? `${existingDescribedBy} ${instructionId}` : instructionId;
  image.setAttribute('aria-describedby', newDescribedBy);

  image.dataset.zoomInstructionAttached = 'true';
}

// create a container and set the full-size image as its background
function createOverlay(image) {
  const overlayImage = document.createElement('img');
  overlayImage.setAttribute('src', `${image.src}`);
  overlay = document.createElement('div');
  prepareOverlay(overlay, overlayImage);

  image.style.opacity = '50%';
  toggleLoadingSpinner(image);

  let overlayLoadedResolve = null;
  const loaded = new Promise((resolve) => {
    overlayLoadedResolve = resolve;
  });

  overlayImage.onload = () => {
    toggleLoadingSpinner(image);
    image.parentElement.insertBefore(overlay, image);
    image.style.opacity = '100%';
    overlayLoadedResolve();
  };

  return [overlay, loaded];
}

function prepareOverlay(container, image) {
  container.setAttribute('class', 'image-magnify-full-size');
  container.setAttribute('aria-label', getZoomStrings().enabled);
  container.style.backgroundImage = `url('${image.src}')`;
  container.setAttribute('role', 'application');
  container.setAttribute('aria-roledescription', 'Image zoom viewer');
  container.setAttribute('tabindex', '0');
}

function toggleLoadingSpinner(image) {
  const loadingSpinner = image.parentElement.parentElement.querySelector(`.loading__spinner`);
  loadingSpinner.classList.toggle('hidden');
}

function moveWithHover(image, event, zoomRatio) {
  const ratio = image.height / image.width;
  const container = event.target.getBoundingClientRect();
  const xPosition = event.clientX - container.left;
  const yPosition = event.clientY - container.top;
  const xPercent = `${xPosition / (image.clientWidth / 100)}%`;
  const yPercent = `${yPosition / ((image.clientWidth * ratio) / 100)}%`;

  overlay.style.backgroundPosition = `${xPercent} ${yPercent}`;
  overlay.style.backgroundSize = `${image.width * zoomRatio}px`;
}

function moveWithKeys(key) {
  if (!overlay) return;
  const currentPos = overlay.style.backgroundPosition || '50% 50%';
  const [x, y] = currentPos.split(' ').map((v) => parseFloat(v));
  const panStep = 5; // 5% pan step

  let newX = x;
  let newY = y;

  if (key === 'ArrowLeft') {
    newX = Math.max(0, x - panStep);
  }
  if (key === 'ArrowRight') {
    newX = Math.min(100, x + panStep);
  }
  if (key === 'ArrowUp') {
    newY = Math.max(0, y - panStep);
  }
  if (key === 'ArrowDown') {
    newY = Math.min(100, y + panStep);
  }

  overlay.style.backgroundPosition = `${newX}% ${newY}%`;
}

function magnify(image, zoomRatio, event) {
  if (overlay) {
    return;
  }
  const [overlayElement, overlayLoaded] = createOverlay(image);
  overlay = overlayElement;

  // Hide zoom instruction while zoomed
  const instruction = image.nextElementSibling;
  if (instruction && instruction.classList.contains('zoom-instruction')) {
    instruction.textContent = getZoomStrings().enabled;
  }

  // Store original aria-describedby and temporarily update it
  const originalDescribedBy = image.getAttribute('aria-describedby');
  const instructionId = instruction ? instruction.id : null;
  if (instructionId && originalDescribedBy) {
    const updatedDescribedBy = originalDescribedBy.replace(instructionId, '').trim();
    image.setAttribute('aria-describedby', updatedDescribedBy || '');
  }

  const closeZoom = () => {
    document.removeEventListener('keydown', keydownHandler, { capture: true });

    overlay.remove();
    overlay = null;

    announceToScreenReader(getZoomStrings().closed);

    // Temporarily change the instruction text to "Zoom closed"
    if (instruction && instruction.classList.contains('zoom-instruction')) {
      instruction.removeAttribute('hidden');
      instruction.textContent = getZoomStrings().closed;
    }

    // Restore original aria-describedby immediately since we want the "Zoom closed" to be read
    if (originalDescribedBy) {
      image.setAttribute('aria-describedby', originalDescribedBy);
    }

    image.focus();

    setTimeout(() => {
      if (instruction && instruction.classList.contains('zoom-instruction')) {
        instruction.textContent = getZoomStrings().activate;
      }
    }, 4200); // Keep "Zoom closed" for 4 seconds
  };

  const keydownHandler = (e) => {
    if (e.key === 'Escape' || e.key === 'Tab') {
      e.preventDefault();
      e.stopPropagation();
      closeZoom();
      return false;
    }

    if (document.activeElement === overlay || overlay.contains(document.activeElement)) {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
        moveWithKeys(e.key);
        return false;
      }
    }
  };

  overlay.onclick = closeZoom;
  overlay.onmouseleave = closeZoom;
  overlay.onmousemove = (e) => moveWithHover(image, e, zoomRatio);

  document.addEventListener('keydown', keydownHandler, { capture: true });

  if (event) {
    moveWithHover(image, event, zoomRatio);
  } else {
    overlay.style.backgroundPosition = '50% 50%';
    overlay.style.backgroundSize = `${image.width * zoomRatio}px`;
  }

  announceToScreenReader(getZoomStrings().enabled);

  overlayLoaded.then(() => {
    setTimeout(() => {
      overlay.focus();
      announceToScreenReader(getZoomStrings().enabled);
    }, 50);
  });
}

function enableZoomOnHover(zoomRatio) {
  const images = document.querySelectorAll('.image-magnify-hover');
  images.forEach((image, index) => {
    image.setAttribute('tabindex', '0');
    image.setAttribute('role', 'button');
    image.setAttribute('aria-haspopup', 'true');

    createZoomInstruction(image, index);

    image.onclick = (event) => {
      magnify(image, zoomRatio, event);
    };

    image.onkeydown = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        magnify(image, zoomRatio);
      }
    };
  });
}

enableZoomOnHover(2);
